import type { BillData } from "@/api/types";
import { resolveBillFileMimeType } from "@/utils/billFileValidation";
import {
  composeAddress,
  parseUkAddressParts,
} from "@/utils/quoteMapper";

const BILL_NAME_HONORIFICS = new Set([
  "mr",
  "mrs",
  "ms",
  "miss",
  "dr",
  "prof",
  "sir",
  "mx",
  "master",
]);

function titleCaseNameToken(token: string): string {
  return token
    .split("-")
    .map((part) => {
      if (!part) return part;
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("-");
}

/** First usable given name from a utility-bill account name (skips titles and initials). */
export function greetingNameFromBill(customerName?: string | null): string | null {
  if (!customerName) return null;

  const given: string[] = [];
  for (const part of customerName.trim().split(/\s+/)) {
    const stripped = part.replace(/[.,]/g, "");
    if (!stripped) continue;
    if (BILL_NAME_HONORIFICS.has(stripped.toLowerCase())) continue;
    given.push(stripped);
  }

  const firstUsable = given.find((part) => part.length > 1);
  return firstUsable ? titleCaseNameToken(firstUsable) : null;
}

/**
 * Name to show in an empty contact field after a bill upload.
 * Signed-in visitors keep their account name; a name already typed is left alone.
 */
export function nameAfterBillUpload(options: {
  currentName: string;
  billName?: string | null;
  signedIn: boolean;
}): string {
  if (options.signedIn || options.currentName.trim()) return options.currentName;
  return options.billName?.trim() || "";
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function buildFormPatchFromBillData(data: BillData) {
  const rawAddress = (data.address ?? "").trim();
  const parts = rawAddress ? parseUkAddressParts(rawAddress) : null;
  const composed = parts ? composeAddress(parts) : "";

  return {
    billData: data,
    annualKwh: data.annual_kwh ?? 3500,
    energySupplier: data.energy_supplier ?? "",
    ...(parts
      ? {
          address1: parts.address1,
          address2: parts.address2,
          city: parts.city,
          region: parts.region,
          address: composed,
          ...(parts.postcode ? { postcode: parts.postcode } : {}),
        }
      : {}),
    ...(data.customer_name ? { customerName: data.customer_name } : {}),
  };
}

type ExtractBillFn = (input: {
  fileUrl: string;
  fileName: string;
  fileType: string;
}) => Promise<BillData>;

export async function processBillUpload<T extends Record<string, unknown>>(
  file: File,
  extractBill: ExtractBillFn,
  update: (patch: Partial<T>) => void,
): Promise<void> {
  const dataUrl = await fileToDataUrl(file);
  update({
    billFile: file,
    billFileUrl: dataUrl,
  } as unknown as Partial<T>);

  try {
    const data = await extractBill({
      fileUrl: dataUrl,
      fileName: file.name,
      fileType: resolveBillFileMimeType(file),
    });
    update(buildFormPatchFromBillData(data) as unknown as Partial<T>);
  } catch {
    // Silently fall back to manual entry
  }
}
